import React, { FC, useState } from 'react';
import AddComment from './AddComment';
import Comments from './Comments';
import styles from '../AnnotationForm.module.scss';
import { Button, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Permissions } from '../../../../../shared/models/permissions.enum';
import { UseHaveAnyPermissions } from '../../../../../Auth/UseHaveAnyPermissions';
import useAuth from '../../../../../Auth/UseAuth';
import { CommentDto } from './AnnotationComments.types';

type Props = {
  comment: CommentDto;
  parentIds: string[];
  addedFailed: boolean;
  addedSuccessfully: boolean;
  onAddComment: (parentIds: string[], message: string) => void;
  onDeleteComment: (parentIds: string[], id: string) => void;
  onEditComment: (parentIds: string[], message: string, id: string) => void;
};

const Comment: FC<Props> = ({ comment, parentIds, addedFailed, addedSuccessfully, onAddComment, onDeleteComment, onEditComment }) => {
  const [replyMode, setReplyMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const { userId } = useAuth();

  const canAddComment = UseHaveAnyPermissions([Permissions.AnnotationManager_AddComments]);
  const havePermissionToDeleteOwnComment = UseHaveAnyPermissions([Permissions.AnnotationManager_DeleteComments]);
  const havePermissionToDeleteAllComment = UseHaveAnyPermissions([Permissions.AnnotationManager_DeleteAllComments]);
  const canDeleteComment = (havePermissionToDeleteOwnComment && comment.userId === userId) || havePermissionToDeleteAllComment;

  const populatedParentIds = [...parentIds, comment.id];

  const onDelete = () => {
    Modal.confirm({
      title: 'Confirm',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => onDeleteComment(parentIds, comment.id)
    });
  };

  const onEdit = () => {
    setEditMode(true);
  };

  const onSaveComment = (parentIds: string[], message: string) => {
    editMode ? onEditComment(parentIds, comment.id, message) : onAddComment(parentIds, message);
    setEditMode(false);
  };

  const hasReplies = comment.replies && !!comment.replies.length;

  return (
    <div className={styles.commentContainer}>
      {editMode ? (
        <AddComment
          disabled={!canAddComment}
          addedFailed={addedFailed}
          addedSuccessfully={addedSuccessfully}
          onSaveComment={onSaveComment}
          onCancelComment={() => setEditMode(false)}
          parentIds={parentIds}
          message={comment.message}
        />
      ) : (
        <>
          <div className={styles.commentContent}>
            <span>
              <span>{comment.userName}</span>
              <span dangerouslySetInnerHTML={{ __html: comment.message }}></span>
            </span>
          </div>
          <div className={styles.commentActions}>
            <Button name='reply' type='link' disabled={!canAddComment} onClick={() => setReplyMode(!replyMode)}>
              Reply
            </Button>
            <Button name='edit' type='link' disabled={!canDeleteComment} onClick={() => onEdit()}>
              Edit
            </Button>
            <Button name='delete' type='link' disabled={!canDeleteComment || hasReplies} onClick={onDelete}>
              Delete
            </Button>
            <span>{comment.dateCreated}</span>
          </div>
        </>
      )}
      <div>
        {replyMode && (
          <AddComment
            disabled={!canAddComment}
            addedFailed={addedFailed}
            addedSuccessfully={addedSuccessfully}
            onCancelComment={() => setReplyMode(false)}
            parentIds={populatedParentIds}
            onSaveComment={onSaveComment}
          />
        )}
      </div>
      <div>
        <Comments
          addedFailed={addedFailed}
          addedSuccessfully={addedSuccessfully}
          comments={comment.replies}
          parentIds={populatedParentIds}
          onAddComment={onAddComment}
          onDeleteComment={onDeleteComment}
          onEditComment={onEditComment}
        />
      </div>
    </div>
  );
};

export default Comment;
